/**
 * usePaneTiles — single React hook the PaneShell mounts.
 *
 * Wraps the five API calls with React state + optimistic updates,
 * and reads the per-user event stream at /api/v1/pane/events (with
 * fetch, bearer in the header) for live tile pushes from any agent.
 *
 * No TanStack Query because the dataset is small (<100 tiles per
 * user) and the SSE-driven update path needs a custom reducer
 * that bypasses query cache invalidation anyway.
 *
 * OCC retry policy: on 409 we refetch the list and re-apply the
 * user's intent against the fresh version. Capped at 3 tries so a
 * permanent conflict surfaces instead of looping.
 *
 * SSE reducer: events with version <= local version are ignored to
 * prevent stomping in-flight optimistic updates from this client.
 */
import { API_BASE } from "@/api/baseUrl";
import * as React from "react";
import { useApiClient } from "@/api/useApiClient";
import { useAuth } from "@/auth/useAuth";
import {
  TileUpdateError,
  clearTiles,
  createTile,
  deleteTile,
  listTiles,
  updateTile,
} from "./api";
import { readSse } from "./sse";
import type { CreateTileBody, PaneTile, UpdateTileBody } from "./types";
import { apiErrorMessage } from "@/lib/api-error";

const OCC_MAX_RETRIES = 3;


/**
 * Wire shape of /api/v1/pane/events. Mirrors backend
 * omni.modules.pane.bridge.PaneEvent.
 */
export interface PaneEventWire {
  kind: "create" | "update" | "delete" | "clear";
  user_id: string;
  tile?: PaneTile | null;
  tile_id?: string | null;
}

export interface PaneTilesApi {
  tiles: PaneTile[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (body: CreateTileBody) => Promise<PaneTile | null>;
  update: (tileId: string, patch: Omit<UpdateTileBody, "expected_version">) => Promise<PaneTile | null>;
  remove: (tileId: string) => Promise<void>;
  /** Remove the tiles; with `keepPinned` (the dashboard's Clear) only the unpinned. */
  clear: (keepPinned?: boolean) => Promise<void>;
}

export function usePaneTiles(): PaneTilesApi {
  const client = useApiClient();
  const auth = useAuth();
  const [tiles, setTiles] = React.useState<PaneTile[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  // Latest-tiles ref so async mutators read the freshest version
  // without re-creating themselves on every render.
  const tilesRef = React.useRef<PaneTile[]>([]);
  tilesRef.current = tiles;

  const refresh = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fresh = await listTiles(client);
      setTiles(fresh);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [client]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  // ── Live updates ───────────────────────────────────────────────────
  // One stream per mount, read with fetch so the bearer goes in the
  // Authorization header (EventSource can only put it in the URL, which
  // lands in access logs). The server does not replay events sent while
  // the stream was down, so every (re)connect re-reads the tile list.
  const getIdTokenRef = React.useRef(auth.getIdToken);
  getIdTokenRef.current = auth.getIdToken;
  const refreshRef = React.useRef(refresh);
  refreshRef.current = refresh;

  React.useEffect(() => {
    const controller = new AbortController();
    let retryTimer: number | null = null;
    let attempts = 0;
    let connectedBefore = false;

    const apply = (raw: string) => {
      try {
        const event = JSON.parse(raw) as PaneEventWire;
        applyEventToLocal(setTiles, tilesRef.current, event);
      } catch {
        /* malformed event — ignore */
      }
    };

    const connect = async () => {
      if (controller.signal.aborted) return;
      try {
        const token = await getIdTokenRef.current().catch(() => null);
        await readSse(
          `${API_BASE}/api/v1/pane/events`,
          token,
          controller.signal,
          () => {
            attempts = 0;
            // The first connect follows the mount-time load; later ones
            // catch up on whatever changed while disconnected.
            if (connectedBefore) void refreshRef.current();
            connectedBefore = true;
          },
          (e) => {
            if (e.event === "create" || e.event === "update" || e.event === "delete" || e.event === "clear") {
              apply(e.data);
            }
          },
        );
      } catch {
        if (controller.signal.aborted) return;
      }
      if (controller.signal.aborted) return;
      // Stream ended or failed — back off and reconnect.
      const backoff = Math.min(1000 * Math.pow(1.5, attempts), 30000);
      attempts++;
      retryTimer = window.setTimeout(() => void connect(), backoff);
    };

    void connect();
    return () => {
      controller.abort();
      if (retryTimer != null) clearTimeout(retryTimer);
    };
  }, []); // stable — token and refresh read via refs

  const create = React.useCallback(
    async (body: CreateTileBody): Promise<PaneTile | null> => {
      try {
        const tile = await createTile(client, body);
        // De-dupe by id: the SSE ``create`` echo for this same tile can
        // land BEFORE this POST resolves (it's broadcast the moment the
        // server writes the row). An unconditional append would then add
        // the tile twice — two rows sharing an id, which is what made
        // delete/move hit both. Append only if the echo hasn't already.
        setTiles((prev) =>
          prev.some((t) => t.id === tile.id) ? prev : [...prev, tile],
        );
        return tile;
      } catch (e) {
        setError(apiErrorMessage(e));
        return null;
      }
    },
    [client],
  );

  const update = React.useCallback(
    async (
      tileId: string,
      patch: Omit<UpdateTileBody, "expected_version">,
    ): Promise<PaneTile | null> => {
      // Optimistic — assume success, roll back on failure. Track the
      // pre-mutation snapshot so the rollback puts the row back as
      // it was.
      const before = tilesRef.current.find((t) => t.id === tileId);
      if (!before) return null;

      // Apply optimistic change locally with a synthetic +1 version
      // so concurrent drags within this client see the bump too.
      setTiles((prev) =>
        prev.map((t) =>
          t.id === tileId
            ? ({ ...t, ...patch, version: t.version + 1 } as PaneTile)
            : t,
        ),
      );

      let expectedVersion = before.version;
      let lastErr: TileUpdateError | null = null;
      for (let attempt = 0; attempt < OCC_MAX_RETRIES; attempt++) {
        try {
          const result = await updateTile(client, tileId, {
            ...patch,
            expected_version: expectedVersion,
          });
          // Replace optimistic row with server-authoritative one
          // (server bumps version, sets updated_at, etc.).
          setTiles((prev) =>
            prev.map((t) => (t.id === tileId ? result : t)),
          );
          return result;
        } catch (e) {
          lastErr = e instanceof TileUpdateError ? e : null;
          if (!lastErr || !lastErr.isConflict) break;
          // 409 — refetch to learn the current version + retry.
          try {
            const fresh = await listTiles(client);
            setTiles(fresh);
            const refreshed = fresh.find((t) => t.id === tileId);
            if (!refreshed) break;
            expectedVersion = refreshed.version;
          } catch {
            break;
          }
        }
      }

      // All retries failed — roll back the optimistic change.
      setTiles((prev) =>
        prev.map((t) => (t.id === tileId ? before : t)),
      );
      setError(lastErr?.message ?? "Failed to update tile");
      return null;
    },
    [client],
  );

  const remove = React.useCallback(
    async (tileId: string): Promise<void> => {
      const before = tilesRef.current;
      // Optimistic removal.
      setTiles((prev) => prev.filter((t) => t.id !== tileId));
      try {
        await deleteTile(client, tileId);
      } catch (e) {
        // Rollback.
        setTiles(before);
        setError(apiErrorMessage(e));
      }
    },
    [client],
  );

  const clear = React.useCallback(async (keepPinned = false): Promise<void> => {
    const before = tilesRef.current;
    setTiles(keepPinned ? before.filter((t) => t.pinned) : []);
    try {
      await clearTiles(client, keepPinned);
    } catch (e) {
      setTiles(before);
      setError(apiErrorMessage(e));
    }
  }, [client]);

  return { tiles, loading, error, refresh, create, update, remove, clear };
}

/**
 * Apply a single SSE event to the tile list.
 *
 * Conflict resolution: on ``update`` events, if the local tile has
 * an equal-or-higher version than the broadcast tile, the event is
 * dropped. This protects in-flight optimistic updates from being
 * stomped by an out-of-order broadcast triggered by another tab.
 *
 * Exported only so the dev test rig can drive it directly; not
 * part of the public hook API.
 */
export function applyEventToLocal(
  setTiles: React.Dispatch<React.SetStateAction<PaneTile[]>>,
  current: PaneTile[],
  event: PaneEventWire,
): void {
  switch (event.kind) {
    case "create": {
      if (!event.tile) return;
      const tile = event.tile;
      // De-dupe INSIDE the functional updater against the LATEST state.
      // The optimistic ``create()`` and this SSE echo race: the ``current``
      // snapshot (tilesRef) is stale until React re-renders, so checking
      // only it misses the just-added tile and appends a duplicate with
      // the same id — which then makes delete/move hit both rows (shared
      // React key). Checking ``prev`` inside the updater closes the race.
      setTiles((prev) =>
        prev.some((t) => t.id === tile.id) || current.some((t) => t.id === tile.id)
          ? prev
          : [...prev, tile],
      );
      return;
    }
    case "update": {
      if (!event.tile) return;
      const incoming = event.tile;
      setTiles((prev) =>
        prev.map((t) => {
          if (t.id !== incoming.id) return t;
          // Older or equal broadcast → keep local (optimistic) state.
          if (t.version >= incoming.version) return t;
          return incoming;
        }),
      );
      return;
    }
    case "delete": {
      if (!event.tile_id) return;
      setTiles((prev) => prev.filter((t) => t.id !== event.tile_id));
      return;
    }
    case "clear": {
      setTiles([]);
      return;
    }
  }
}
